import styled from '@emotion/styled'

import {
  Container,
  type BackgroundColor,
  type ContainerProps,
  type Space,
} from '@/components/Container'

import { ListDefault, type ListDefaultProps } from '@/components/List/Default'

import { HeadingL, BodyM } from '@/components/Typography'
import { clearBuilderProps } from '@/core/utils/dom/clearBuilderProps'
import type { TitleProps } from '@/models'
import { CONFIG } from '@/styles/emotion.config'

export type ListBlockDefaultProps = {
  id?: string
  title: TitleProps
  subtitle: TitleProps
  subtitleWeight: 'regular' | 'bold'
  size: string
  position: 'right' | 'center' | 'left'
  background: BackgroundColor
  space: Space
}

export type ListBlockProps = ListBlockDefaultProps & ListDefaultProps

const ListBlockStyles = styled(Container)`
  margin-bottom: 1.5rem;

  .item__text,
  .item__icon {
    font-size: 1.5rem;
    line-height: 1.75rem;
    letter-spacing: -0.6px;
  }

  .list__content-wrapper {
    margin-bottom: 2rem;
    .list__title {
      font-size: 3rem;
      margin-bottom: 2rem;
      line-height: 3rem;

      ${CONFIG.media.mobileLg} {
        font-size: 2rem;
        line-height: 2.5rem;
        margin-bottom: 1.25rem;
      }
    }

    .list__subtitle {
      font-size: 1.5rem;
      margin-bottom: 2rem;
      line-height: 2rem;

      ${CONFIG.media.mobileLg} {
        font-size: 1.25rem;
        line-height: 1.5rem;
        margin-bottom: 1rem;
      }
    }
  }
`

function ListPremiumBlock<T extends ListBlockProps>({
  id,
  background,
  title,
  subtitle,
  space,
  ...listProps
}: T) {
  clearBuilderProps(listProps)

  const containerProps: ContainerProps = {
    spaceOut: space,
    spaceIn: 'm',
    backgroundColor: background,
    id,
  }

  return (
    <ListBlockStyles {...containerProps}>
      <div className="list__content-wrapper">
        {!!title && (
          <HeadingL tag={title.tag} className="list__title">
            {title.text}
          </HeadingL>
        )}

        {!!subtitle && <BodyM className="list__subtitle" tag="" html={subtitle.text} />}
      </div>

      {listProps.items && (
        <ListDefault
          customBullet="check"
          className="list"
          {...listProps}
          columns="two"
          color="dark-high"
        />
      )}
    </ListBlockStyles>
  )
}

export default ListPremiumBlock
